<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import BaseSpinner from './BaseSpinner.vue'

defineOptions({ inheritAttrs: false })

interface Props {
  variant?: 'primary' | 'outline' | 'ghost' | 'danger'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  to?: string | object
  href?: string
  loading?: boolean
  disabled?: boolean
  type?: 'button' | 'submit' | 'reset'
  block?: boolean
  testid?: string
  rounded?: string
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  to: undefined,
  href: undefined,
  type: 'button',
  loading: false,
  disabled: false,
  block: false,
  testid: undefined,
  rounded: undefined
})

const baseClasses = "relative cursor-pointer inline-flex items-center justify-center gap-2 font-bold transition-[background-color,color,border-color,transform,opacity,box-shadow] duration-150 ease-lux focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed overflow-hidden"

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'primary':
      return "bg-primary hover:bg-primary-hover text-white border border-primary-hover/50"
    case 'outline':
      return "bg-surface hover:bg-bg-page text-text-mid border border-border-standard hover:border-primary/50 hover:-translate-y-px active:translate-y-0"
    case 'ghost':
      return "bg-transparent hover:bg-primary/5 text-text-muted hover:text-primary border border-transparent active:translate-y-0"
    case 'danger':
      return "bg-error-bg hover:bg-error text-error-text hover:text-white border border-error-text/20 hover:border-error-text/50 hover:-translate-y-px active:translate-y-0"
    default:
      return ""
  }
})

const radiusButton = computed(() => props.rounded ? `rounded-${props.rounded}` : '')

const sizeClasses = computed(() => {
  const r = radiusButton.value
  switch (props.size) {
    case 'sm': return `text-xs px-4 py-2 min-h-button-height-sm ${r || 'rounded-button'}`
    case 'md': return `text-sm px-6 py-3 min-h-button-height-md ${r || 'rounded-button'}`
    case 'lg': return `text-base px-8 py-4 min-h-button-height-lg ${r || 'rounded-button'}`
    case 'xl': return `text-base px-10 py-6 min-h-button-height-xl ${r || 'rounded-button'}`
    default: return ""
  }
})


const isLink = computed(() => !!props.to || !!props.href)
const componentType = computed(() => {
  if (props.to) return RouterLink
  if (props.href) return 'a'
  return 'button'
})
</script>

<template>
  <component
    :is="componentType"
    v-bind="$attrs"
    :to="to"
    :href="href"
    :type="!isLink ? type : undefined"
    :disabled="!isLink ? (disabled || loading) : undefined"
    :aria-disabled="disabled || loading ? 'true' : undefined"
    :aria-busy="loading ? 'true' : undefined"
    :data-testid="testid || 'base-button'"
    :class="[
      baseClasses, 
      variantClasses, 
      sizeClasses, 
      block ? 'w-full flex' : 'inline-flex',
      (disabled || loading) && isLink ? 'pointer-events-none opacity-60' : ''
    ]"
  >
    <div
      v-if="loading"
      role="status"
      aria-live="polite"
      class="absolute inset-0 flex items-center justify-center bg-surface/40 backdrop-blur-overlay z-10"
    >
      <BaseSpinner
        :size="size === 'sm' ? 'sm' : 'md'"
        color="muted"
      />
      <span class="sr-only">Memuat...</span>
    </div>
    
        <span :class="['flex items-center justify-center gap-2 w-full transition-opacity duration-150', loading ? 'opacity-0' : 'opacity-100']">
      <slot name="prefix" />
      <slot />
      <slot name="suffix" />
    </span>
  </component>
</template>
